import { IDocumentCategory, VendorOrderDocumentNames } from '../../types/order/documents/IDocumentCategoryType';
import { IVendorDocuments } from '../../components/vendorDashboard/StatusSection';
import { eVendorTitleSearchType } from '../../types/order/documents/shared/eVendorTitleSearchType';
import { IStandardDocumentData } from '../../types/order/documents/shared/interfaces';
import { vendorTitleSearchDocumentsLabels } from './vendorDocuments';

interface RequiredAndOptional {
  requiredDocuments: IVendorDocuments[];
  optionalDocuments: IVendorDocuments[];
}

const buildIsVendorTitleSearchCategory =
  (category: IDocumentCategory) =>
  //this function tells us whether a document satisfies a certain category.
  (document: IStandardDocumentData): boolean => {
    // we keep this check so that we can add new vendorTitleSearch categories in the future.
    if (document.category === category.name) return true;

    // it could be the case that we have a legacy document that satisfies the category.
    const keyCategoryName = VendorOrderDocumentNames.find((names) => Object.values(names).includes(category.name));
    return Object.values(keyCategoryName ?? {}).includes(document.category);
  };

const createVendorDocument = (
  vendorTitleSearchCategory: IDocumentCategory,
  orderDocuments: IStandardDocumentData[]
): IVendorDocuments => {
  const isVendorTitleSearchCategory = buildIsVendorTitleSearchCategory(vendorTitleSearchCategory);
  const isOptional = vendorTitleSearchCategory.vendorTitleSearch === eVendorTitleSearchType.OPTIONAL;
  return {
    label: vendorTitleSearchDocumentsLabels(vendorTitleSearchCategory, isOptional),
    document: orderDocuments.find(isVendorTitleSearchCategory),
  };
};

const buildReducer = (orderDocuments: IStandardDocumentData[]) => {
  return (accumulator: RequiredAndOptional, current: IDocumentCategory): RequiredAndOptional => {
    if (!current.vendorTitleSearch) return accumulator;
    if (current.vendorTitleSearch === eVendorTitleSearchType.UPLOADABLE) return accumulator;

    if (current.vendorTitleSearch === eVendorTitleSearchType.REQUIRED)
      return {
        ...accumulator,
        requiredDocuments: [...accumulator.requiredDocuments, createVendorDocument(current, orderDocuments)],
      };
    return {
      ...accumulator,
      optionalDocuments: [...accumulator.optionalDocuments, createVendorDocument(current, orderDocuments)],
    };
  };
};

export const findRequiredAndOptionalVendorDocuments = (
  activeDocumentCategories: IDocumentCategory[],
  orderDocuments: IStandardDocumentData[]
): RequiredAndOptional => {
  const reducer = buildReducer(orderDocuments);
  return activeDocumentCategories.reduce(reducer, { requiredDocuments: [], optionalDocuments: [] });
};

export const findRequiredAndOptionalVendorDocumentsHelperMethods = {
  buildIsVendorTitleSearchCategory,
  createVendorDocument,
  buildReducer,
};
